import { useQuery } from '@tanstack/react-query';
import {
  getTemplates,
  validateHtmlTemplateV2,
  templateReportStatus,
  generalTemplateReport,
  topUnoptimizedTemplates,
  topUnoptimizedCategories,
  getTemplateEditHistory,
} from '../services/templates';
import { useState } from 'react';
import useDebounce from 'react-use/lib/useDebounce';
import { useTemplateListingStore } from '../containers/Mail/Templates/store';

export const useFetchEmailTemplatesRaw = filter => {
  return useQuery(['templates', filter], () => getTemplates(filter));
};

export const useFetchEmailTemplates = () => {
  const {
    search,
    page,
    pageSize,
    categoryId,
    emailTypeId,
    onlyActive,
    onlyAmp,
    setPage,
    sortColumn,
  } = useTemplateListingStore();
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Set page to 1 when filter changes
  useDebounce(
    () => {
      setPage(1);
    },
    500,
    [search, categoryId, emailTypeId, onlyActive, onlyAmp, sortColumn],
  );
  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search],
  );

  const filter = {
    search: debouncedSearch,
    page,
    limit: pageSize,
    categoryId,
    activeOnly: onlyActive,
    ampOnly: onlyAmp,
    emailTypeId,
    sortColumn,
  };
  const queryFilter = ['templates', filter];

  return [useFetchEmailTemplatesRaw(filter), queryFilter];
};

export const useGetTemplateEditHistory = templateId => {
  return useQuery(
    ['templateEditHistory', { templateId }],
    () => getTemplateEditHistory(templateId),
    {
      enabled: !!templateId,
      keepPreviousData: true,
      staleTime: 0,
      cacheTime: 0,
    },
  );
};

export const useValidateHtml = template => {
  return useQuery(
    ['validateHtml', { template }],
    () => validateHtmlTemplateV2({ template }),
    {
      enabled: !!template,
      keepPreviousData: true,
      staleTime: 0,
      cacheTime: 0,
    },
  );
};

export const useGetTemplateReportStatus = () => {
  return useQuery(['templateReportStatus'], () => templateReportStatus(), {
    keepPreviousData: true,
    staleTime: 0,
    cacheTime: 0,
    refetchInterval: 5000,
  });
};

export const useGetGeneralTemplateReport = () => {
  return useQuery(['generalTemplateReport'], generalTemplateReport, {
    keepPreviousData: true,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
};

export const useGetTopUnoptimizedTemplates = () => {
  return useQuery(['unoptimizedTemplates'], topUnoptimizedTemplates, {
    keepPreviousData: true,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
};

export const useGetTopUnoptimizedCategories = () => {
  return useQuery(['unoptimizedCategories'], topUnoptimizedCategories, {
    keepPreviousData: true,
    refetchOnMount: 'always',
    refetchOnWindowFocus: 'always',
  });
};
